import React, { useCallback, useState } from 'react';
import { batch } from 'react-redux';
import { Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';
import JSZip from 'jszip';

import { UPLOAD_VIDEO_CONFIG } from '@assetManagement/data/uploadVideoConfig';
import ASLVideoUploadTable from '../ASLVideoUploadTable';
import ASLVideoExtraction from '../ASLVideoExtraction';
import getVideoDuration from '@assetManagement/utils/getVideoDuration';
import Loader from 'components/Loader';
import validateVideo from '@assetManagement/utils/validateVideo';

function ASLVideoUploadMain() {
  const [isExtractingFile, setIsExtractingFile] = useState(false);
  const [areFilesExtracted, setAreFilesExtracted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [zipFile, setZipFile] = useState(null);
  const [videoFiles, setVideoFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const resetZipFile = useCallback(() => {
    batch(() => {
      setZipFile(null);
      setAreFilesExtracted(false);
      setVideoFiles([]);
      setHasError(false);
      setIsExtractingFile(false);
    });
  }, []);

  const extractFiles = async () => {
    if (!zipFile) {
      cogoToast.error('Please upload a ZIP file first.');
      return;
    }

    try {
      setIsExtractingFile(true);
      const zip = await JSZip.loadAsync(zipFile);
      const successFiles = [];
      const extractedVideoFiles = [];
      setHasError(false);

      const supportedVideoExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.wmv'];

      await Promise.all(
        Object.keys(zip.files).map(async (filename) => {
          try {
            const file = zip.files[filename];
            const isBlackFile = file.name.includes('__MACOSX') || false;
            if (isBlackFile) return;

            const isVideoFile = supportedVideoExtensions.some((ext) =>
              filename.toLowerCase().endsWith(ext)
            );
            if (!isVideoFile) {
              return;
            }

            const content = await file.async('blob');
            const videoFile = new File([content], filename, {
              type: 'video/mp4',
              lastModified: file.date.getTime(),
            });
            const duration = await getVideoDuration(videoFile);
            videoFile.duration = duration;

            const errors = await validateVideo(videoFile, UPLOAD_VIDEO_CONFIG);
            if (errors?.uploadVideo?.length > 0) {
              videoFile.error = errors;
              setHasError(true);
            }

            extractedVideoFiles.push(videoFile);
            successFiles.push(filename);
          } catch (entryError) {
            console.log('entryError', entryError);
          }
        })
      );

      if (extractedVideoFiles.length === 0) {
        cogoToast.error(
          'No video files found in the ZIP file. Please check the file and try again.'
        );
        resetZipFile();
        setIsExtractingFile(false);
        return;
      }
      setVideoFiles(extractedVideoFiles);
      setAreFilesExtracted(true);
      setIsExtractingFile(false);
    } catch (zipError) {
      setIsExtractingFile(false);
      cogoToast.error(
        'Failed to extract ZIP file. Please check the file and try again.'
      );
    }
  };

  return (
    <div>
      <ASLVideoExtraction
        setZipFile={setZipFile}
        resetZipFile={resetZipFile}
        zipFile={zipFile}
      />
      {!areFilesExtracted && (
        <Tappable
          className="btn btn-primary"
          onClick={extractFiles}
          disabled={!zipFile || isExtractingFile}
        >
          {isExtractingFile ? 'Extracting...' : 'Extract Files'}
        </Tappable>
      )}
      {isExtractingFile && <Loader />}
      {videoFiles?.length > 0 && (
        <ASLVideoUploadTable
          videoFiles={videoFiles}
          zipFile={zipFile}
          hasError={hasError}
          setIsUploading={setIsUploading}
          isUploading={isUploading}
          resetZipFile={resetZipFile}
        />
      )}
    </div>
  );
}

export default ASLVideoUploadMain;
